import React, { useState } from 'react';
import {
  Box, Table, Thead, Tbody, Tr, Th, Td, Badge, Button, HStack,
  Select, useToast, IconButton
} from '@chakra-ui/react';
import { DeleteIcon, EditIcon } from '@chakra-ui/icons';
import { adminService } from '../../services/api';

export const UserManagement = ({ users, onUpdate, onEdit, currentUserRole }) => {
  const [updating, setUpdating] = useState(null);
  const toast = useToast();

  const canManageUser = (targetRole) => {
    if (currentUserRole === 'manager') return targetRole === 'user';
    if (currentUserRole === 'super_admin') return true;
    return false;
  };

  const handleRoleChange = async (userId, newRole) => {
    setUpdating(userId);
    try {
      const response = await adminService.updateUser(userId, { role: newRole });
      onUpdate(userId, response.data.user);
      toast({ title: 'Role updated', status: 'success', duration: 2000 });
    } catch (err) {
      toast({
        title: 'Update failed',
        description: err.response?.data?.error,
        status: 'error',
        duration: 3000
      });
    } finally {
      setUpdating(null);
    }
  };

  const handleToggleActive = async (userId, currentStatus) => {
    setUpdating(userId);
    try {
      const response = await adminService.updateUser(userId, { isActive: !currentStatus });
      onUpdate(userId, response.data.user);
      toast({ title: currentStatus ? 'User deactivated' : 'User activated', status: 'success', duration: 2000 });
    } catch (err) {
      toast({
        title: 'Update failed',
        description: err.response?.data?.error,
        status: 'error',
        duration: 3000
      });
    } finally {
      setUpdating(null);
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Deactivate this user?')) return;

    setUpdating(userId);
    try {
      await adminService.deleteUser(userId);
      onUpdate(userId, { is_active: false });
      toast({ title: 'User deactivated', status: 'success', duration: 2000 });
    } catch (err) {
      toast({
        title: 'Delete failed',
        description: err.response?.data?.error,
        status: 'error',
        duration: 3000
      });
    } finally {
      setUpdating(null);
    }
  };

  return (
    <Box overflowX="auto">
      <Table variant="simple">
        <Thead>
          <Tr>
            <Th>Username</Th>
            <Th>Email</Th>
            <Th>Name</Th>
            <Th>Role</Th>
            <Th>Status</Th>
            <Th>Created</Th>
            <Th>Actions</Th>
          </Tr>
        </Thead>
        <Tbody>
          {users.map(user => (
            <Tr key={user.id} opacity={user.is_active ? 1 : 0.6}>
              <Td fontWeight="medium">{user.username}</Td>
              <Td>{user.email}</Td>
              <Td>{user.first_name} {user.last_name}</Td>
              <Td>
                {currentUserRole === 'super_admin' ? (
                  <Select
                    size="sm"
                    value={user.role}
                    onChange={(e) => handleRoleChange(user.id, e.target.value)}
                    isDisabled={updating === user.id || !canManageUser(user.role)}
                    w="140px"
                  >
                    <option value="user">User</option>
                    <option value="manager">Manager</option>
                    <option value="super_admin">Admin</option>
                  </Select>
                ) : (
                  <Badge colorScheme={
                    user.role === 'super_admin' ? 'red' :
                    user.role === 'manager' ? 'purple' : 'gray'
                  }>
                    {user.role === 'super_admin' ? 'Admin' :
                     user.role === 'manager' ? 'Manager' : 'User'}
                  </Badge>
                )}
              </Td>
              <Td>
                <Badge colorScheme={user.is_active ? 'green' : 'red'}>
                  {user.is_active ? 'Active' : 'Inactive'}
                </Badge>
              </Td>
              <Td fontSize="sm" color="gray.600">
                {new Date(user.created_at).toLocaleDateString()}
              </Td>
              <Td>
                <HStack spacing={1}>
                  <IconButton
                    icon={<EditIcon />}
                    size="sm"
                    variant="ghost"
                    colorScheme="blue"
                    aria-label="Edit user"
                    onClick={() => onEdit(user)}
                    isDisabled={updating === user.id || !canManageUser(user.role)}
                  />
                  <Button
                    size="xs"
                    variant="outline"
                    colorScheme={user.is_active ? 'orange' : 'green'}
                    onClick={() => handleToggleActive(user.id, user.is_active)}
                    isLoading={updating === user.id}
                    isDisabled={!canManageUser(user.role)}
                  >
                    {user.is_active ? 'Deactivate' : 'Activate'}
                  </Button>
                  <IconButton
                    icon={<DeleteIcon />}
                    size="sm"
                    variant="ghost"
                    colorScheme="red"
                    aria-label="Delete"
                    onClick={() => handleDelete(user.id)}
                    isDisabled={updating === user.id || !canManageUser(user.role)}
                  />
                </HStack>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </Box>
  );
};

export default UserManagement;
